// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Action from 'core/fsmpack/statemachine/actions/Action';
import AwsSystem from 'aws/AwsSystem';
import SystemBus from 'core/entities/SystemBus';
import SystemBusMessage from 'core/entities/SystemBusMessage';
import FsmUtils from 'core/fsmpack/statemachine/FsmUtils';

// spell-checker: words Cognito

/**
 * @import Context from 'core/api/common/Context';
 * @import State from 'core/fsmpack/statemachine/State';
 */

/**
 * Base class for Lex Processing actions.
 */
class LexProcessingAction extends Action {

  /**
   * Creates a new LexProcessingAction.
   * @param {Object} options Options for action.
   */
  constructor(options) {
    super(...arguments); // also support (id, settings) parameters used with FSM

    this._userId = FsmUtils.guid();

    SystemBus.addListener(AwsSystem.SDK_READY, () => {
      this._lex = new AWS.LexRuntime();
      if(AWS.config.credentials && AWS.config.credentials.identityId) {
        // If using Cognito(which is the only supported way to use Lex with Sumerian)
        // Then use our identity Id as the userId
        this._userId = AWS.config.credentials.identityId;
      }

    }, true);
  }

  /**
   * Gets the transition label for a provided transition key.
   * @param {string} transitionKey Transition key to get label for.
   * @returns {string} Transition label for the provided transition key.
   * @private
   */
  static getTransitionLabel(transitionKey) {
    switch (transitionKey) {
      case 'responseReady': return 'On Response Ready';
      case 'lexProcessingError': return 'On Processing Error';
      case 'lexNotConfigured':
      default:
        return 'On Lex Configuration Error';
    }
  }

  /**
   * Called when the state that contains the action is entered. This function is
   * never called when the action is added, even if the action is being added to
   * an active state, unless the {@link State#addAction} enterIfActive option is
   * set.
   *
   * @param {Context} fsm Provides access to the state that contains this action.
   * @private
   */
  enter(fsm) {
    if (!this._lex) {
      SystemBus.emit('sumerian.warning', {
        title: `AWS SDK Missing`,
        message: 'AWS SDK is not available. Please ensure a Cognito Identity Pool ID with the correct permissions has been added to the AWS Configuration Panel.',
      });
      fsm.send(this.transitions.lexNotConfigured);
      return;
    }

    const entity = fsm.getOwnerEntity();
    return this._process(fsm, entity);
  }

  /**
   * Called when the world beings to play, or when an action is added while the
   * world is playing or paused.
   *
   * @param {Context} fsm Provides access to the state that contains this action.
   * @private
   */
  setup(fsm) {
    const entity = fsm.getOwnerEntity();
    if (!entity.dialogueComponent) {
      SystemBus.emit('sumerian.warning', {
        title: `Dialogue Component Missing`,
        message: 'Dialogue Component not found on entity. Add the Dialogue Component to the entity and select a bot name and alias.',
      });
      fsm.send(this.transitions.lexNotConfigured);
      return;
    }
  }

  _getContentType() {
    throw new Error('Abstract Method Not implemented');
  }

  _getLexConfig(entity, inputStream) {
    const { name, alias } = entity.dialogueComponent;
    const contentType = this._getContentType();

    return {
      accept: 'text/plain; charset=utf-8',
      botName: name,
      botAlias: alias,
      contentType,
      inputStream,
      userId: this._userId
    };
  }

  _processWithLex(fsm, entity, lexSettings) {
    return new Promise((resolve, reject) => {
      this._lex.postContent(lexSettings, (error, data) => {
        if (error) {
          return reject(error);
        }

        return resolve(data);
      });
    }).then(data => {
      if (this.logInput) {
        const input = typeof lexSettings.inputStream === 'string'
          ? lexSettings.inputStream
          : data.inputTranscript;
        console.log(`[${this.actionName}] Input:`, input);
      }

      if (!data.message) {
        if (data.encodedMessage) {
          if (this.logResponse) {
            console.log(`[${this.actionName}] Translating encodedMessage:`, data.encodedMessage);
          }

          data.message = decodeURIComponent(escape(atob( data.encodedMessage )));
        }
        else {
          data.message = '';
        }
      }

      if (this.logResponse) {
        console.log(`[${this.actionName}] Response:`, data.message);
      }

      entity.setData('lexResponse', data.message);

      SystemBus.emit(`${SystemBusMessage.LEX_RESPONSE}.${entity.id}`, data);

      fsm.send(this.transitions.responseReady);

      return data;
    }).catch(error => {
      console.error(error);
      fsm.send(this.transitions.lexProcessingError);
      return error;
    });
  }

  _process(fsm, entity) {
    throw new Error('Abstract Method Not implemented');
  }
}

export const baseActionConfig = {
  canTransition: true,
  parameters: [
    {
      name: 'Log user input',
      key: 'logInput',
      type: 'boolean',
      description: 'Prints the user input in the debug console of your browser.',
      default: false,
    },
    {
      name: 'Log bot response',
      key: 'logResponse',
      type: 'boolean',
      // spell-checker: words bot's
      description: "Prints the bot's response in the debug console of your browser.",
      default: false,
    },
  ],
  transitions: [
    {
      key: 'responseReady',
      description: 'Transition point to send lex response to next state.',
    },
    {
      key: 'lexProcessingError',
      description: 'Transition point to handle lex error responses.',
    },
    {
      key: 'lexNotConfigured',
      description: 'Transition point to handle lex problems.',
    },
  ],
};

export default LexProcessingAction;
